import React, { useEffect, useState } from 'react';
import './OrderFlowEarningCalculator.css'; 

const OrderFlowEarningCalculator = ({ param1, param2, maxLevel = 8, refsPerLevel = 5 }) => {
  const levels = Array.from({ length: maxLevel }, (_, i) => i + 1);
  const [overallCommission, setOverallCommission] = useState(0);

  const param2Number = Number(param2);
  let commissionRates = param2Number === 10
    ? [0.10, 0.02, 0.005, 0.005, 0.005, 0.005, 0.005, 0.005]
    : [0.20, 0.04, 0.01, 0.01, 0.01, 0.01, 0.01, 0.01];

  // Limit commissionRates array to maxLevel
  commissionRates = commissionRates.slice(0, maxLevel);

  const calculateCommission = (price, level) => {
    const rate = commissionRates[level - 1];
    const commission = price * rate * Math.pow(refsPerLevel, level);
    return commission.toFixed(2);
  };

  useEffect(() => {
    let totalCommission = 0;
    levels.forEach((level) => {
      const rate = commissionRates[level - 1];
      const commission = param1 * rate * Math.pow(refsPerLevel, level);
      totalCommission += commission;
    });
    setOverallCommission(totalCommission.toFixed(2));
  }, [param1, param2, maxLevel, refsPerLevel]);

  const convertToIndianWords = (num) => {
    const a = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
      'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

    const formattedNum = Math.floor(Number(num)).toString();
    if (!formattedNum || isNaN(formattedNum)) return 'Invalid number';
    if (formattedNum === '0') return 'Zero';
    if (formattedNum.length > 12) return 'number too large';

    let words = [];
    const length = formattedNum.length;

    if (length > 7) {
      const crorePart = formattedNum.slice(0, length - 7);
      if (crorePart) words.push(getWords(crorePart) + ' Crore');
    }
    if (length > 5) {
      const lakhPart = formattedNum.slice(length - 7, length - 5);
      if (lakhPart) words.push(getWords(lakhPart) + ' Lakh');
    }
    if (length > 3) {
      const thousandPart = formattedNum.slice(length - 5, length - 3);
      if (thousandPart) words.push(getWords(thousandPart) + ' Thousand');
    }
   if (length > 2) {
    const hundredPart = formattedNum.slice(length - 3, length - 2);
    if (hundredPart !== '0') {
        words.push(getWords(hundredPart) + ' hundred');
    }
    }

    const lastTwoDigits = formattedNum.slice(-2);
    if (lastTwoDigits) words.push(getWords(lastTwoDigits));

    return words.join(' ').trim();
  };

  const getWords = (numStr) => {
    const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (numStr.length === 1) return a[Number(numStr)];
    if (numStr.length === 2) {
      if (numStr[0] === '1') return a[Number(numStr)];
      return (b[Number(numStr[0])] + (numStr[1] !== '0' ? ' ' + a[Number(numStr[1])] : '')).trim();
    }
    return '';
  };

  return (
    <div className="w-full inset-0 bg-black px-2 py-0 rounded-lg shadow-lg mt-1 overflow-x-auto">
      <table className="table-auto w-full text-white text-sm border border-blue-500">
        <thead>
          <tr>
            <th className="px-2 py-2 text-left">Level : Rate</th>
            <th className="px-2 py-2 text-left">Referrals : Incentive/Referral</th>
            <th className="px-2 py-2 text-left">Total Earning (₹)</th>
          </tr>
        </thead>
        <tbody>
          {levels.map((level) => (
            <tr key={level}>
              <td className="border px-2 py-2 text-left">
                {level} : {(commissionRates[level - 1] * 100).toFixed(2)}%
              </td>
              <td className="border px-2 py-2 text-left">
                {refsPerLevel}<sup>{level}</sup> = {Math.pow(refsPerLevel, level)} : [<span className="text-yellow-200">{(param1 * commissionRates[level - 1]).toFixed(2)}</span>]
              </td>
              <td className="border px-2 py-2 text-left">{calculateCommission(param1, level)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="text-yellow-200 text-sm mt-2">
        Total Estimated Incentive: ₹ {Number(overallCommission).toFixed(2).toLocaleString('en-IN')}
      </p>
      <p className="text-white text-sm border-b border-blue-400">
        In words: {convertToIndianWords(overallCommission)} Rupees
      </p>

      <p className="text-gray-200 mt-2 text-xs lg:text-sm">
        <ol className="list-decimal list-inside space-y-1 [&_li]:text-gray-200">
          <li>Get your product FREE - You refer (sale) to {refsPerLevel}</li>
          <li>Earn PASSIVE INCOME - Up to level {maxLevel}</li>
          <li>Instant income for Direct Referrals as Active Income. Passive income for the current month will be transferred into your account within 10 working days next month.</li>
          <li>New Physical, Digital Products, Services, Subscriptions will be added regularly</li>
          <li>Estimated in ₹. INCLUSIVE 18% GST</li>
          <li>TDS as per income tax rules, if applicable.</li>
        </ol>
      </p>
    </div>
  );
};

export default OrderFlowEarningCalculator;
